import { useEffect, useMemo } from 'react';

interface ThemeColors {
  bgColor: string;
  textColor: string;
  hintColor: string;
  linkColor: string;
  buttonColor: string;
  buttonTextColor: string;
  secondaryBgColor: string;
  headerBgColor: string;
  accentTextColor: string;
  sectionBgColor: string;
  sectionHeaderTextColor: string;
  subtitleTextColor: string;
  destructiveTextColor: string;
}

const DEFAULT_LIGHT_THEME: ThemeColors = {
  bgColor: '#ffffff',
  textColor: '#000000',
  hintColor: '#999999',
  linkColor: '#2481cc',
  buttonColor: '#3390ec',
  buttonTextColor: '#ffffff',
  secondaryBgColor: '#f1f1f1',
  headerBgColor: '#ffffff',
  accentTextColor: '#3390ec',
  sectionBgColor: '#ffffff',
  sectionHeaderTextColor: '#6d6d71',
  subtitleTextColor: '#999999',
  destructiveTextColor: '#ff3b30',
};

const DEFAULT_DARK_THEME: ThemeColors = {
  bgColor: '#212121',
  textColor: '#ffffff',
  hintColor: '#aaaaaa',
  linkColor: '#8cc2ed',
  buttonColor: '#8cc2ed',
  buttonTextColor: '#ffffff',
  secondaryBgColor: '#0f0f0f',
  headerBgColor: '#212121',
  accentTextColor: '#8cc2ed',
  sectionBgColor: '#212121',
  sectionHeaderTextColor: '#8e8e93',
  subtitleTextColor: '#aaaaaa',
  destructiveTextColor: '#ff453a',
};

// Check if running in Telegram
const checkIsTelegram = (): boolean => {
  return typeof window !== 'undefined' && !!window.Telegram?.WebApp?.initData;
};

export const useTheme = () => {
  const colors = useMemo((): ThemeColors => {
    // Check if we're in Telegram
    if (checkIsTelegram() && window.Telegram?.WebApp?.themeParams) {
      const tp = window.Telegram.WebApp.themeParams;
      const isDark = window.Telegram.WebApp.colorScheme === 'dark';
      const defaultTheme = isDark ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;

      return {
        bgColor: tp.bg_color || defaultTheme.bgColor,
        textColor: tp.text_color || defaultTheme.textColor,
        hintColor: tp.hint_color || defaultTheme.hintColor,
        linkColor: tp.link_color || defaultTheme.linkColor,
        buttonColor: tp.button_color || defaultTheme.buttonColor,
        buttonTextColor: tp.button_text_color || defaultTheme.buttonTextColor,
        secondaryBgColor: tp.secondary_bg_color || defaultTheme.secondaryBgColor,
        headerBgColor: tp.header_bg_color || defaultTheme.headerBgColor,
        accentTextColor: tp.accent_text_color || defaultTheme.accentTextColor,
        sectionBgColor: tp.section_bg_color || defaultTheme.sectionBgColor,
        sectionHeaderTextColor: tp.section_header_text_color || defaultTheme.sectionHeaderTextColor,
        subtitleTextColor: tp.subtitle_text_color || defaultTheme.subtitleTextColor,
        destructiveTextColor: tp.destructive_text_color || defaultTheme.destructiveTextColor,
      };
    }

    // Development mode - use system preference
    const prefersDark = typeof window !== 'undefined'
      && window.matchMedia('(prefers-color-scheme: dark)').matches;

    return prefersDark ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
  }, []);

  useEffect(() => {
    // Apply CSS variables to document root
    const root = document.documentElement;

    root.style.setProperty('--tg-theme-bg-color', colors.bgColor);
    root.style.setProperty('--tg-theme-text-color', colors.textColor);
    root.style.setProperty('--tg-theme-hint-color', colors.hintColor);
    root.style.setProperty('--tg-theme-link-color', colors.linkColor);
    root.style.setProperty('--tg-theme-button-color', colors.buttonColor);
    root.style.setProperty('--tg-theme-button-text-color', colors.buttonTextColor);
    root.style.setProperty('--tg-theme-secondary-bg-color', colors.secondaryBgColor);
    root.style.setProperty('--tg-theme-header-bg-color', colors.headerBgColor);
    root.style.setProperty('--tg-theme-accent-text-color', colors.accentTextColor);
    root.style.setProperty('--tg-theme-section-bg-color', colors.sectionBgColor);
    root.style.setProperty('--tg-theme-section-header-text-color', colors.sectionHeaderTextColor);
    root.style.setProperty('--tg-theme-subtitle-text-color', colors.subtitleTextColor);
    root.style.setProperty('--tg-theme-destructive-text-color', colors.destructiveTextColor);

    // Apply background color to body
    document.body.style.backgroundColor = colors.bgColor;
    document.body.style.color = colors.textColor;
  }, [colors]);

  return colors;
};

// Type declaration is in src/types/telegram.d.ts
